import React from 'react';

/**
 * C1 — Multi-service picker (checkbox-style).
 *
 * Props:
 *   - services: catalog rows for the store (array of { serviceUuid, serviceName, appointmentDurationMins, active })
 *   - selectedUuids: array of currently-selected uuids
 *   - onToggle: (uuid) => void
 *   - maxAllowed: number — when reached, further unselected tiles are disabled
 *   - disabled: boolean — disables all tiles
 *
 * Renders the totals row underneath: count, summed duration.
 * Single-select behavior emerges naturally when maxAllowed=1.
 */
const ServicePickerMultiSelect = ({
  services,
  selectedUuids = [],
  onToggle,
  maxAllowed = 3,
  disabled = false,
}) => {
  const activeServices = (services || []).filter((s) => s.active !== false);
  const selectedSet = new Set(selectedUuids);

  const totalDurationMins = activeServices
    .filter((s) => selectedSet.has(s.serviceUuid))
    .reduce((sum, s) => sum + (Number(s.appointmentDurationMins) || 30), 0);

  const capReached = selectedUuids.length >= maxAllowed;

  if (activeServices.length === 0) {
    return (
      <div className="walkin-modal-no-services">
        No active services available
      </div>
    );
  }

  return (
    <>
      <div className="walkin-modal-services">
        {activeServices.map((service) => {
          const isSelected = selectedSet.has(service.serviceUuid);
          const isDisabled = disabled || (!isSelected && capReached);
          return (
            <button
              key={service.serviceUuid}
              className={`walkin-modal-service-tile ${isSelected ? 'selected' : ''}`}
              onClick={() => onToggle(service.serviceUuid)}
              disabled={isDisabled}
              type="button"
              aria-pressed={isSelected}
              aria-label={`${service.serviceName}${isSelected ? ' (selected)' : ''}`}
            >
              <div className="walkin-modal-service-tile-row">
                <span
                  className={`walkin-modal-service-checkbox ${isSelected ? 'checked' : ''}`}
                  aria-hidden="true"
                >
                  {isSelected ? '✓' : ''}
                </span>
                <div className="walkin-modal-service-name">{service.serviceName}</div>
              </div>
              <div className="walkin-modal-service-meta">
                {service.appointmentDurationMins || 30} mins
              </div>
            </button>
          );
        })}
      </div>
      <div
        className="walkin-modal-services-totals"
        data-testid="walkin-services-totals"
      >
        <span className="walkin-modal-services-totals-count">
          {selectedUuids.length}/{maxAllowed} selected
        </span>
        {selectedUuids.length > 0 && (
          <span className="walkin-modal-services-totals-duration">
            · {totalDurationMins} min total
          </span>
        )}
      </div>
    </>
  );
};

export default ServicePickerMultiSelect;
